import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import '../ReportingAndAnalytics.less';
import { REPORTING_CONSTANTS } from './reportingAndAnalyticsConstants';
import { formatMinutesToDHMS } from '../../../utils/reportingUtility';
import { ReactComponent as ReportingCountRightIcon } from '../../../assets/images/svg/ReportingCountRightIcon.svg';
import { ReactComponent as ReportingCountDownIcon } from '../../../assets/images/svg/ReportingCountDownIcon.svg';
import { TatMetricCardGroupProps } from '../../../shared/interfaces/reportingAndAnalyticsInterface';

export const TatMetricCardGroup: React.FC<TatMetricCardGroupProps> = ({
  data,
  config,
  getSections,
  setActiveSection,
  availableStatus = [],
  expandedSections,
  setExpandedSections,
}: any): ReactElement => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevSectionsRef = useRef<{ key: string; label: string }[]>([]);

  // Toggle open/close of a section
  const toggleOpen = (key: string, index: number): void => {
    setExpandedSections((prev: Record<string, boolean>): Record<string, boolean> => ({
      ...prev,
      [key]: !prev[key],
    }));

    const ref = sectionRefs?.current?.[index];
    if (ref) {
      setTimeout((): void => {
        ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  };

  // Map status IDs to labels
  const statusLabelMap = useMemo(
    (): Record<string, string> =>
      Object.fromEntries(
        (availableStatus ?? [])
          ?.filter((status: Record<string, any>): boolean => status?.value != null)
          ?.map((status: Record<string, any>): any => [String(status?.value), status?.label ?? '']),
      ),
    [availableStatus],
  );

  // Prepare navigation sections for sidebar with sub groups
  const navSections = useMemo(
    (): {
      key: string;
      label: string;
      subgroups: { key: string; label: string }[];
    }[] =>
      Object?.entries(data || {})?.map(([sectionKey, sectionData]: any): any => {
        const sectionLabel =
          statusLabelMap[sectionKey] ||
          config?.[sectionKey]?.Total?.title ||
          sectionKey;

        const subgroups = Object?.keys(
          sectionData?.transactionFlows || {},
        )?.map((flowName: string): Record<string, string> => ({
          key: `${sectionKey}--${flowName}`,
          label: flowName,
        }));

        return {
          key: sectionKey,
          label: sectionLabel,
          subgroups,
        };
      }),
    [data, config, statusLabelMap],
  );

  useEffect((): void => {
    if (!isEqual(prevSectionsRef.current, navSections)) {
      getSections?.(navSections);
      prevSectionsRef.current = navSections;
    }
  }, [navSections, getSections]);

  useEffect((): any => {
    if (!setActiveSection) {
      return;
    }

    const viewer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        const visibleEntry = entries
          ?.filter((element: IntersectionObserverEntry): boolean => element?.isIntersecting)
          ?.sort(
            (a: IntersectionObserverEntry, b: IntersectionObserverEntry): number =>
              a.boundingClientRect.top - b.boundingClientRect.top,
          )[0];

        const visibleId = visibleEntry?.target?.getAttribute('id');
        if (visibleId) {
          setActiveSection(visibleId);
        }
      },
      {
        threshold: REPORTING_CONSTANTS.NAVIGATION_FIRING_THRESHOLD,
        rootMargin: REPORTING_CONSTANTS.NAVIGATION_ROOT_MARGIN,
      },
    );

    sectionRefs?.current?.forEach((element: any): void => element && viewer.observe(element));
    return (): void => {
      sectionRefs?.current?.forEach((element: any): void => element && viewer.unobserve(element));
    };
  }, [navSections, setActiveSection, expandedSections]);

  const renderMetricCard = (
    metricKey: string,
    metricValue: any,
    sectionConfig: any,
  ): ReactElement => {
    const displayData = sectionConfig?.[metricKey] || {};
    const tat = metricValue?.tatInMinutes ?? metricValue?.count ?? '-';

    return (
      <div className="metric-card" key={metricKey}>
        <div className="Transaction-card-title">
          {displayData?.title || metricKey}
        </div>
        <div className="count-text">{(tat === REPORTING_CONSTANTS.ZERO_STRING) ? `-` : formatMinutesToDHMS(tat)}</div>
      </div>
    );
  };

  return (
    <div className="metric-group-wrapper">
      {Object?.entries(data || {})?.map(
        ([sectionKey, sectionData]: any, index: number): ReactElement => {
          const sectionConfig = config?.[sectionKey] || {};
          const isOpen = !!expandedSections[sectionKey];

          return (
            <div
              key={sectionKey}
              className={`section-wrapper section-card ${
                isOpen ? 'open' : 'closed'
              }`}
              id={sectionKey}
              ref={(element: any): void => {
                sectionRefs.current[index] = element;
              }}
            >
              <button
                type="button"
                className={`section-header ${
                  isOpen ? 'section-header--open' : 'section-header--closed'
                }`}
                onClick={(): void => toggleOpen(sectionKey, index)}
                aria-expanded={isOpen}
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => {
                  e.preventDefault();
                }}
              >
                <span className="section-header__icon">
                  {isOpen ? (
                    <ReportingCountDownIcon />
                  ) : (
                    <ReportingCountRightIcon />
                  )}
                </span>
                <span className="section-header-title">
                  {statusLabelMap?.[sectionKey] ??
                    sectionConfig?.Total?.title ??
                    sectionKey}
                </span>
              </button>

              {isOpen ? (
                <div >
                  {Object?.entries(sectionData?.transactionFlows || {})?.map(
                    ([flowName, flowMetrics]: any, subIndex: number, arr: any[]): ReactElement => (
                      <div key={flowName} className="metrics-subgroup">
                        <div className="subgroup-title">{flowName}</div>
                        <div className="metrics-grid">
                          {Object?.entries(flowMetrics)?.map(
                            ([
                              metricKey,
                              metricValue,
                            ]: any): ReactElement | null => {
                              if (typeof metricValue === 'object') {
                                return renderMetricCard(
                                  metricKey,
                                  metricValue,
                                  sectionConfig,
                                );
                              }
                              return null;
                            },
                          )}
                        </div>
                        <hr className={`hr-reporting ${subIndex === arr?.length - 1 ? 'hr-transparent' : ''}`} />
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div />
              )}
            </div>
          );
        },
      )}
      <div className="h-800" />
    </div>
  );
};
